/**
 * Generates HTML email template for team member invitation.
 * Sent when an organization admin invites team members from the Welcome page.
 * Uses shared layout from emailLayout.js for header/footer.
 */

import { spacer, emailDocOpen, emailDocClose, emailHeader, emailFooter, icon } from '../emailLayout';

/**
 * Build the team invite email HTML
 *
 * @param {Object} params
 * @param {string} params.firstName - Invited member's first name
 * @param {string} params.lastName - Invited member's last name
 * @param {string} params.email - Invited member's email
 * @param {string} params.designation - Invited member's role/designation
 * @param {string} params.companyName - Organization name
 * @param {string} params.inviterName - Name of the person sending the invite
 * @param {string} params.registrationUrl - URL for the invited member to register
 * @returns {string} HTML email body
 */
export const buildTeamInviteEmailHtml = ({
  firstName,
  lastName,
  email,
  designation,
  companyName,
  inviterName,
  registrationUrl,
  organization,
}) => {
  const memberName = [firstName, lastName].filter(Boolean).join(' ');

  return `${emailDocOpen({ title: `You've been invited to join ${companyName}` })}

${emailHeader({ title: `You've been invited to join ${companyName}`, organization, isBroker: true })}

${spacer(0)}

<!-- Member & Invited Via -->
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;">
  <tr>
    <td style="padding:24px 32px 0;">
      <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
        <tr>
          <td style="width:50%;vertical-align:top;">
            <p style="margin:0 0 2px;font-size:12px;text-transform:uppercase;color:#6B7280;letter-spacing:0.5px;font-weight:600;">Member</p>
            <p style="margin:0;font-size:14px;color:#111827;font-weight:600;">${memberName}</p>
          </td>
          <td style="width:50%;vertical-align:top;text-align:right;">
            <p style="margin:0 0 2px;font-size:12px;text-transform:uppercase;color:#6B7280;letter-spacing:0.5px;font-weight:600;">Invited Via</p>
            <p style="margin:0;font-size:14px;color:#111827;font-weight:600;">${email}</p>
          </td>
        </tr>
      </table>
    </td>
  </tr>
  <tr>
    <td style="padding:16px 32px 0;">
      <div style="border-top:1px solid #EBEBEB;"></div>
    </td>
  </tr>
</table>

${spacer(0)}

<!-- Invite Message -->
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;">
  <tr>
    <td style="padding:24px 32px 0;">
      <table role="presentation" cellpadding="0" cellspacing="0">
        <tr>
          <td width="56" valign="top">
            <!-- Colored <div> for the circle + icon() helper for the
                 hosted SVG. Inline data:image/svg+xml URIs got stripped
                 by Gmail webmail and some Outlook builds — rendering as
                 a broken image in the recipient's inbox. -->
            <div style="width:48px;height:48px;background-color:#DCFCE7;border-radius:50%;text-align:center;line-height:48px;">
              ${icon('userAdd', 24)}
            </div>
          </td>
        </tr>
      </table>
    </td>
  </tr>
  <tr>
    <td style="padding:16px 32px 32px;">
      <p style="margin:0;font-size:14px;color:#374151;line-height:1.7;">
        ${inviterName} from <strong>${companyName}</strong> invited you to join their organization as a team member.
      </p>
    </td>
  </tr>
</table>

${spacer(0)}

<!-- CTA Button -->
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;">
  <tr>
    <td style="padding:0 32px 32px;">
      <table role="presentation" cellpadding="0" cellspacing="0">
        <tr>
          <td style="background-color:#22315e;border-radius:24px;">
            <a href="${registrationUrl}" target="_blank" style="display:inline-block;padding:12px 28px;font-size:14px;color:#FFFFFF;text-decoration:none;font-weight:500;font-family:'Rethink Sans',sans-serif;">
              Join Organization
            </a>
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>

${spacer(20)}

${emailFooter()}

${emailDocClose()}`;
};
